/* eslint-disable camelcase */
import React from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import i18n from 'i18next';

import {
  Features,
  WorkoutType,
  WorkoutResourceType,
} from '@gymcloud/core';

import {
  commonActions,
} from 'web-actions';

import {
  Video,
  Button,
  Author,
  TagList,
  Tooltip,
  TagField,
  TextField,
  EditorField,
  SwitchField,
  MarkdownRenderer,
  WorkoutExercisesList,
  WorkoutExercisesFooter,
  WorkoutExercisesSubHeader,
} from 'web-components';

import {
  Flex,
  H100,
  WorkoutExercisesView,
} from 'web-styled';

import {
  OpenedTooltip,
} from 'web-types';

import * as S from './styled';

type Props = {
  type?: string,
  workout: any,
  readOnly?: boolean,
  fillPath: string,
  features?: Features,
  workoutType?: WorkoutType,
  deleteText?: string,
  workoutAuthor?: any,
  resourceType: WorkoutResourceType,
  openedTooltips?: OpenedTooltip[]

  onRequestSave?: { (): void },
  onRequestSubmit: { (data: any, actions: any): void },
  onRequestDelete?: { (): void },
};

const WorkoutTemplate = ({
  workout,
  fillPath,
  resourceType,
  workoutAuthor,
  type = 'workout',
  readOnly = false,
  features = {},
  deleteText = 'Delete',
  workoutType = 'WorkoutTemplate',
  openedTooltips = [],

  onRequestSave,
  onRequestSubmit,
  onRequestDelete,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return (
    <Flex fillSpace column spacing={20}>
      <Formik
        initialValues={{
          name: workout?.name || '',
          tag_list: workout?.tag_list ? workout.tag_list.join(',') : '',
          video_id: workout?.video_id || '',
          video_url: workout?.video_url || '',
          description: workout?.description || '',
          has_die_roll: workout?.has_die_roll || false,
        }}
        onSubmit={onRequestSubmit}
      >
        {(formikProps) => (
          <Flex
            as="form"
            column
            spacing={20}
            width="100%"
            shrink="0"
            onSubmit={formikProps.handleSubmit}
          >
            <S.Header>
              <Author
                fullName={workout?.author_full_name}
                avatar={workoutAuthor?.user_profile?.avatar?.thumb?.url}
              />
              {readOnly ? (
                <Flex spacing={10}>
                  {openedTooltips?.some(el => el.key === 'edit_workout_button') ? (
                    <Tooltip
                      body="Click Edit to add a description or video, and Save when you are done."
                      toggleMode
                      initialState
                    >
                      <Button
                        ghost
                        appearance="primary"
                        onClick={() => navigate(`${fillPath}/edit`)}
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={() => navigate(`${fillPath}/edit`)}
                    >
                      Edit
                    </Button>
                  )}
                  {onRequestSave && (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onRequestSave}
                    >
                      Save to Library
                    </Button>
                  )}
                  {type === 'workout' && (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={() => {
                        window.print();
                        dispatch(commonActions.triggerAnalytic('clicked_print_button'));
                      }}
                    >
                      Print
                    </Button>
                  )}
                  <Button
                    ghost
                    appearance="danger"
                    onClick={onRequestDelete}
                  >
                    {deleteText}
                  </Button>
                </Flex>
              ) : (
                <Flex spacing={10}>
                  <Button
                    ghost
                    appearance="primary"
                    isLoading={formikProps.isSubmitting}
                    onClick={formikProps.submitForm}
                  >
                    Save
                  </Button>
                  <Button
                    ghost
                    appearance="danger"
                    onClick={() => navigate(fillPath)}
                  >
                    Cancel
                  </Button>
                </Flex>
              )}
            </S.Header>
            <S.WorkoutDescription
              spacing={30}
            >
              <Flex
                column
                flex="0 0 35%"
                align="center"
              >
                <Video
                  {...(readOnly
                    ? {
                      id: workout.video_id,
                      src: workout.video_url,
                      readOnly: true,
                    } : {
                      id: formikProps.values.video_id,
                      src: formikProps.values.video_url,
                    }
                  )}
                  title={workout?.name}
                  height="250px"
                  buildQuery={() => {
                    const name = document.getElementById('name-id') as HTMLInputElement;
                    return name ? name.value : '';
                  }}
                  resourceType={resourceType}
                  onAssign={(video) => {
                    const id = video.embed_url.indexOf('vimeo') !== -1 ? video.id : null;
                    formikProps.setFieldValue('video_id', id);
                    formikProps.setFieldValue('video_url', video.embed_url);
                  }}
                  onUploaded={(videos) => {
                    formikProps.setFieldValue('video_id', videos[0].id);
                    formikProps.setFieldValue('video_url', '');
                  }}
                  onDelete={() => {
                    formikProps.setFieldValue('video_id', '');
                    formikProps.setFieldValue('video_url', '');
                  }}
                />
              </Flex>
              {readOnly ? (
                <Flex
                  column
                  spacing={15}
                  flex="1 1 auto"
                >
                  <H100 color="primary">
                    {workout.name}
                  </H100>

                  <MarkdownRenderer
                    source={workout?.description}
                    maxHeight={180}
                    placeholder={i18n.t('click.to.add.description', { type: i18n.t(type!) })}
                  />
                  <TagList
                    tags={workout?.tag_list}
                    resourceType="workouts"
                  />
                </Flex>
              ) : (
                <Flex
                  column
                  spacing={15}
                  flex="1 1 auto"
                >
                  <Field
                    flex="0 0 60px"
                    name="name"
                    label="Name"
                    color="primary"
                    component={TextField}
                  />
                  <Field
                    name="description"
                    color="primary"
                    label="Description"
                    minRows={5}
                    placeholder={i18n.t('type.description.placeholder', { type: i18n.t(type!) })}
                    component={EditorField}
                  />
                  <Field
                    name="tag_list"
                    component={TagField}
                  />
                  {features?.die_roll && (
                    <Field
                      name="has_die_roll"
                      label="Die Roll"
                      color="primary"
                      component={SwitchField}
                    />
                  )}
                </Flex>
              )}
            </S.WorkoutDescription>
          </Flex>
        )}
      </Formik>
      <S.ExerciseListWrapper>
        <WorkoutExercisesView disabled={!readOnly}>
          <WorkoutExercisesSubHeader
            workoutId={workout.id}
          />
          <WorkoutExercisesList
            type={type!}
            workoutId={workout.id}
            workoutType={workoutType!}
          />
          <WorkoutExercisesFooter
            workoutId={workout.id}
            workoutType={workoutType!}
          />
        </WorkoutExercisesView>
      </S.ExerciseListWrapper>
    </Flex>
  );
};

export default WorkoutTemplate;
